import React, { Fragment } from 'react';
import { Boxes } from 'lucide-react';

import aws_icon from '@root/assets/img/cloud_provider_icons/aws_icon.png';
import AWSServiceIcons, { hasServiceIcon } from '@root/components/awsServiceIcons';
import GCPIcon from '@root/assets/img/header/cloud_providers/gcp.png';
import { azure_icon } from '@root/components/utilities/CloudIconsUtil';
import RegionsFlagIcons from '@root/components/v2/ui-elements/CountryFlagIcons/regionFlagIcons';
import Text from '@root/components/v2/ui-elements/Text';
import Tooltip from '@root/components/v2/ui-elements/Tooltip';
import { useEnvironmentsData } from '@root/hooks/environments';
import COLORS from '@pages/visualization/ForceGraph/color-mapping';
import ICONS from '@pages/visualization/ForceGraph/icon-mapping';
import { getColorFromString } from '@pages/visualization/ForceGraph/utils';
import './style.scss';

const CLOUD_ICONS = { aws: aws_icon, azure: azure_icon, gcp: GCPIcon };
// The cloud icon and tile sizes live in style.scss.
const META_ICON_SIZE = 12;
const TILE_FALLBACK_ICON_SIZE = 14;
const SERVICE_ICON_SIZE = 26;

const isEmptyValue = value => value === undefined || value === null || value === '' || value === '-';

// Square tile with the resource's service icon. An explicit icon (e.g. the K8s cluster icon) replaces it, on its own
// tint or a neutral tile; a service without an icon falls back to the resource type's graph icon and colour.
const ResourceTypeTile = ({ service, resourceType, icon, iconBackground }) => {
  if (icon) {
    return (
      <span className="ResourceCell__Tile ResourceCell__Tile--Custom" style={{ backgroundColor: iconBackground }}>
        {icon}
      </span>
    );
  }

  if (hasServiceIcon(service)) {
    return (
      <span className="ResourceCell__Tile ResourceCell__Tile--Service">
        <AWSServiceIcons
          name={service}
          width={SERVICE_ICON_SIZE}
          height={SERVICE_ICON_SIZE}
          imgWidth={16}
          imgHeight={16}
        />
      </span>
    );
  }

  const color = resourceType?.includes('GCP') ? getColorFromString(resourceType) : COLORS[resourceType] || '#ddd';

  return (
    <span className="ResourceCell__Tile" style={{ backgroundColor: color }}>
      {ICONS[resourceType] ? (
        <img src={ICONS[resourceType]} alt={resourceType} />
      ) : (
        <Boxes size={TILE_FALLBACK_ICON_SIZE} color={color} />
      )}
    </span>
  );
};

// Resource name with an `environment / region / cluster` trail beneath it; segments without a value are dropped.
// `hideClusterIcon` drops the cluster segment's icon when that slot carries something other than a cluster name.
const Resource = ({
  name,
  service,
  resourceType,
  icon,
  iconBackground,
  environmentId,
  regions,
  cluster,
  hideClusterIcon,
}) => {
  const { data } = useEnvironmentsData();
  const environment = data?.data?.find(env => env.id === environmentId);
  const cloud = environment?.cloud_provider ?? 'aws';

  const regionList = (Array.isArray(regions) ? regions : [regions]).filter(region => !isEmptyValue(region));
  const [region] = regionList;

  const segments = [
    !isEmptyValue(environmentId) && {
      key: 'environment',
      tooltip: environmentId,
      icon: CLOUD_ICONS[cloud] && <img src={CLOUD_ICONS[cloud]} alt={cloud} className="ResourceCell__CloudIcon" />,
      label: environmentId,
    },
    region && {
      key: 'region',
      tooltip: regionList.join(', '),
      icon: (
        <RegionsFlagIcons name={region} height={`${META_ICON_SIZE}px`} width={`${META_ICON_SIZE}px`} isRounded={true} />
      ),
      label: regionList.length > 1 ? `${region} +${regionList.length - 1}` : region,
    },
    !isEmptyValue(cluster) && {
      key: 'cluster',
      tooltip: cluster,
      icon: !hideClusterIcon && <Boxes size={META_ICON_SIZE} />,
      label: cluster,
    },
  ].filter(Boolean);

  return (
    <div className="ResourceCell">
      <ResourceTypeTile service={service} resourceType={resourceType} icon={icon} iconBackground={iconBackground} />
      <div className="ResourceCell__Text">
        <Tooltip title={name}>
          <Text fontWeight="medium" className="SingleLine ResourceCell__Name">
            {isEmptyValue(name) ? '-' : name}
          </Text>
        </Tooltip>
        {segments.length > 0 && (
          <div className="ResourceCell__Meta">
            {segments.map((segment, index) => (
              <Fragment key={segment.key}>
                {index > 0 && <span className="ResourceCell__Separator">/</span>}
                <Tooltip title={segment.tooltip} isIcon={true}>
                  <span className="ResourceCell__Segment">
                    {segment.icon}
                    <span className="SingleLine">{segment.label}</span>
                  </span>
                </Tooltip>
              </Fragment>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Resource;
